import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Editable percentage cell that shows a % input and the computed dollar price below.
 * On blur (or Enter), persists the change via the update-item-pricing endpoint.
 *
 * Props:
 *  - resultId
 *  - itemId
 *  - retailPrice
 *  - pct (current % value 0-100)
 *  - price (current $ price)
 *  - field: "selling_pct" | "cost_pct"
 *  - onUpdated(updatedResult)
 *  - className
 */
const EditablePctCell = ({
  resultId,
  itemId,
  retailPrice,
  pct,
  price,
  field,
  onUpdated,
  className = "",
}) => {
  const [value, setValue] = useState(formatPct(pct));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(formatPct(pct));
  }, [pct]);

  const previewPrice = (() => {
    const n = parseFloat(value);
    if (isNaN(n)) return price;
    return retailPrice * (n / 100);
  })();

  const save = async () => {
    const n = parseFloat(value);
    if (isNaN(n) || n < 0 || n > 100) {
      toast.error("Enter a % between 0 and 100");
      setValue(formatPct(pct));
      return;
    }
    if (Math.abs(n - pct) < 0.0001) return; // no change
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/update-item-pricing/${resultId}`,
        { items: [{ item_id: itemId, [field]: n }] }
      );
      onUpdated(response.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to update item");
      setValue(formatPct(pct));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex flex-col space-y-1 ${className}`}>
      <div className="inline-flex items-center">
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") {
              setValue(formatPct(pct));
              e.target.blur();
            }
          }}
          className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right mono-font text-xs disabled:opacity-50"
          data-testid={`item-${field}-input-${itemId}`}
        />
        <span className="ml-1 text-slate-400 text-xs">%</span>
      </div>
      <span className="text-xs text-slate-500 mono-font">
        ${previewPrice.toFixed(2)}
      </span>
    </div>
  );
};

const formatPct = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "";
  return Number(v).toFixed(2);
};

export default EditablePctCell;
